import { formatCurrency } from '@mahawi/eshop-common/src/format-currency';
import { getFlag } from '@mahawi/eshop-common/src/get-flag';
import {
  EOrderStatus,
  type ICurrencyType,
} from '@mahawi/eshop-common/src/types';
import {
  Alert,
  Divider,
  Row,
  Skeleton,
  Space,
  Statistic,
  Typography,
} from 'antd';
import { type ColumnGroupType, type ColumnType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import BubbleComponent from '../components/bubble';
import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import Table from '../components/table';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { getTagByRelativeDate } from '../fragments/get-tag-by-relative-date';
import { renderUpdatedAtMessage } from '../fragments/updated-at-message';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { useCurrencyMap } from '../hooks/use-currency-map';
import { type RootState } from '../reducers';
import { dashboardLoadPendingOrders } from '../reducers/dashboard/reducer';
import {
  ordersLoadByOrderStatusCodes,
  ordersReset,
} from '../reducers/orders/reducer';
import { type IAdminOrder } from '../reducers/orders/types';
import { SPACING } from '../theme/tokens';

const REVENUE_STATUSES: EOrderStatus[] = [
  EOrderStatus.PAID,
  EOrderStatus.PAYED,
  EOrderStatus.PARTIALLY_PAID,
  EOrderStatus.DELIVERED,
  EOrderStatus.PARTIALLY_DELIVERED,
  EOrderStatus.SHIPPED,
  EOrderStatus.PARTIALLY_SHIPPED,
];

interface DashboardStats {
  todayCount: number;
  todayRevenueMap: Map<string, number>;
  weekCount: number;
  weekRevenueMap: Map<string, number>;
}

function computeStats(orders: IAdminOrder[]): DashboardStats {
  const now = dayjs();
  let todayCount = 0;
  let weekCount = 0;
  const todayRevenueMap = new Map<string, number>();
  const weekRevenueMap = new Map<string, number>();

  for (const order of orders) {
    const created = dayjs(order.createdAt);
    const iso = order.currencyPrice.isoCode;
    const price = order.currencyPrice.priceWithoutTax;

    if (created.isSame(now, 'day')) {
      todayCount += 1;
      todayRevenueMap.set(iso, (todayRevenueMap.get(iso) ?? 0) + price);
    }

    if (created.isSame(now, 'week')) {
      weekCount += 1;
      weekRevenueMap.set(iso, (weekRevenueMap.get(iso) ?? 0) + price);
    }
  }

  return { todayCount, todayRevenueMap, weekCount, weekRevenueMap };
}

function formatRevenueMap(
  revenueMap: Map<string, number>,
  currencyByIso: Map<string, ICurrencyType>,
): string {
  if (revenueMap.size === 0) {
    return EMPTY_VALUE;
  }
  return [...revenueMap.entries()]
    .map(([iso, amount]) => {
      const ct = currencyByIso.get(iso);
      return ct ? formatCurrency(amount, ct) : `${amount} ${iso}`;
    })
    .join(' / ');
}

interface OrderRow {
  key: string;
  uuid: string;
  createdAt: Date;
  country: string;
  surname: string;
  amount: string;
}

function buildOrderRow(
  o: IAdminOrder,
  currencyByIso: Map<string, ICurrencyType>,
): OrderRow {
  const ct = currencyByIso.get(o.currencyPrice.isoCode);
  return {
    key: o.uuid,
    uuid: o.uuid,
    createdAt: o.createdAt,
    country: `${getFlag({ countryTypeIsoCode: o.taxCountryTypeIsoCode })} ${o.taxCountryTypeIsoCode}`,
    surname: o.billingAddress.surname,
    amount: ct
      ? formatCurrency(o.currencyPrice.priceWithoutTax, ct)
      : `${o.currencyPrice.priceWithoutTax} ${o.currencyPrice.isoCode}`,
  };
}

const orderTableColumns: (ColumnGroupType<object> | ColumnType<object>)[] = [
  {
    title: 'Date',
    dataIndex: 'createdAt',
    render: (createdAt: Date): React.ReactElement =>
      getTagByRelativeDate({ date: new Date(createdAt) }),
    width: '20%',
  },
  {
    title: 'Customer',
    dataIndex: 'surname',
    width: '25%',
  },
  {
    title: 'Country',
    dataIndex: 'country',
    width: '20%',
  },
  {
    title: 'Amount (ex. tax)',
    dataIndex: 'amount',
    width: '25%',
  },
  {
    title: '',
    dataIndex: 'uuid',
    render: (uuid: string): React.ReactElement => (
      <Link to={`/order/${uuid}`} openInNewTab />
    ),
    width: '10%',
  },
];

function DashboardContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const Orders = useSelector((s: RootState) => s.Orders);
  const Dashboard = useSelector((s: RootState) => s.Dashboard);
  const Currency = useSelector((s: RootState) => s.Currency);
  const currencyByIso = useCurrencyMap(Currency.currencies);

  useEffect((): void => {
    dispatch(ordersReset());
    dispatch(ordersLoadByOrderStatusCodes(REVENUE_STATUSES, 300));
    dispatch(dashboardLoadPendingOrders());
  }, []);

  const stats = useMemo(
    (): DashboardStats => computeStats(Orders.orders ?? []),
    [Orders.orders, Orders.updatedAt],
  );

  const recentRows = useMemo((): OrderRow[] => {
    return [...(Orders.orders ?? [])]
      .sort(
        (a, b): number => dayjs(b.createdAt).unix() - dayjs(a.createdAt).unix(),
      )
      .slice(0, 10)
      .map((o: IAdminOrder): OrderRow => buildOrderRow(o, currencyByIso));
  }, [Orders.orders, Orders.updatedAt, currencyByIso]);

  const pendingRows = useMemo((): OrderRow[] => {
    return [...(Dashboard.pendingOrders ?? [])]
      .sort(
        (a, b): number => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      )
      .map((o: IAdminOrder): OrderRow => buildOrderRow(o, currencyByIso));
  }, [Dashboard.pendingOrders, Dashboard.pendingUpdatedAt, currencyByIso]);

  const isInitialLoad =
    (Orders.inProcess && !Orders.orders) ||
    (Dashboard.pendingInProcess && !Dashboard.pendingOrders);

  return (
    <Space orientation="vertical" size={SPACING.lg} style={{ width: '100%' }}>
      <DetailPageHeader
        title="Dashboard"
        onReload={(): void => {
          dispatch(ordersReset());
          dispatch(ordersLoadByOrderStatusCodes(REVENUE_STATUSES, 300));
          dispatch(dashboardLoadPendingOrders());
        }}
        isReloadDisabled={Orders.inProcess || Dashboard.pendingInProcess}
        isReloadHighlighted={!Orders.orders}
        info={renderUpdatedAtMessage(
          Orders.updatedAt,
          Orders.inProcess,
          (): boolean => Orders.orders !== undefined,
        )}
      />

      {isInitialLoad ? (
        <Skeleton active />
      ) : (
        <>
          <Row gutter={SPACING.lg}>
            <BubbleComponent span={6}>
              <Statistic title="Today's orders" value={stats.todayCount} />
            </BubbleComponent>
            <BubbleComponent span={6}>
              <Statistic
                title="Today's revenue (ex. tax)"
                value={formatRevenueMap(stats.todayRevenueMap, currencyByIso)}
              />
            </BubbleComponent>
            <BubbleComponent span={6}>
              <Statistic title="This week's orders" value={stats.weekCount} />
            </BubbleComponent>
            <BubbleComponent span={6}>
              <Statistic
                title="This week's revenue (ex. tax)"
                value={formatRevenueMap(stats.weekRevenueMap, currencyByIso)}
              />
            </BubbleComponent>
          </Row>

          <Divider />

          {pendingRows.length > 0 && (
            <>
              <Alert
                type="warning"
                showIcon
                title={`${pendingRows.length} order${pendingRows.length === 1 ? '' : 's'} awaiting payment`}
              />
              <Table
                columns={orderTableColumns}
                dataSource={pendingRows}
                isLoading={Dashboard.pendingInProcess}
                emptyText="No pending orders"
              />
              <Divider />
            </>
          )}

          <Typography>
            <Typography.Title level={4}>Last 10 orders</Typography.Title>
          </Typography>

          <Table
            columns={orderTableColumns}
            dataSource={recentRows}
            isLoading={Orders.inProcess}
            emptyText="No orders found"
          />
        </>
      )}
    </Space>
  );
}

export default DashboardContainer;
